import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";

const BRONZE = "#A89880";
const PERKS = [
  "On-demand training programs to choose from",
  "Beautiful high-protein recipe library",
  "Cycle syncing — food & training in rhythm with your body",
  "Members-only community Q&A",
  "Full nutrition & progress tracking",
];

export function FenyxSection() {
  const [form, setForm] = useState({ name: "", email: "", instagram: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);

  useEffect(() => {
    api.get("/public/config").then((r) => setLive(!!r.data.fenyx_live)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/fenyx-waitlist", form);
      setDone(true);
    } catch {
      setDone(true);
    } finally { setBusy(false); }
  };

  return (
    <section id="fenyx" className="px-6 md:px-12 py-16 md:py-24" style={{ background: "#141311" }} data-testid="fenyx-section">
      <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="lp-label-sm" style={{ color: BRONZE }}>{live ? "Now Open · The Membership" : "Coming Soon · The Membership"}</p>
          <h2 className="lp-h text-5xl md:text-6xl mt-5" style={{ color: "#F5F1EA" }}>FENYX</h2>
          <p className="lp-it text-2xl md:text-3xl mt-2" style={{ color: BRONZE }}>rise from the ashes.</p>
          <p className="mt-6 leading-relaxed" style={{ color: "#B5AC9E" }}>
            Not ready for coaching yet? FENYX is the self-guided membership from Wellness Way
            with DJ — the same intentional standard, in an app you carry every day. One low
            monthly price. Zero guesswork.
          </p>
          <ul className="mt-7 space-y-3">
            {PERKS.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[15px]" style={{ color: "#D8D2C7" }}>
                <Check size={14} className="mt-1 flex-shrink-0" style={{ color: BRONZE }} /> {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="p-8 md:p-10" style={{ border: "1px solid rgba(168,152,128,0.35)" }}>
          {live ? (
            <div data-testid="fenyx-live-cta">
              <p className="lp-label-sm" style={{ color: BRONZE }}>Doors are open</p>
              <div className="flex items-end gap-2 mt-5">
                <span className="lp-h text-6xl" style={{ color: "#F5F1EA" }}>$30</span>
                <span className="text-sm mb-2" style={{ color: "#B5AC9E" }}>/ month · cancel anytime</span>
              </div>
              <p className="mt-5 text-sm leading-relaxed" style={{ color: "#B5AC9E" }}>
                Create your account, check out securely, and you're inside in under two minutes.
              </p>
              <Link to="/fenyx" data-testid="fenyx-join-btn"
                className="block text-center mt-7 py-4 lp-label-sm transition-opacity hover:opacity-85"
                style={{ background: BRONZE, color: "#141311" }}>Join FENYX Now</Link>
            </div>
          ) : done ? (
            <div data-testid="fenyx-success">
              <p className="lp-label-sm" style={{ color: BRONZE }}>You're in</p>
              <h3 className="lp-h text-3xl mt-4" style={{ color: "#F5F1EA" }}>Welcome to the waitlist.</h3>
              <p className="mt-4 leading-relaxed" style={{ color: "#B5AC9E" }}>
                Check your inbox — founding members will be the first through the door, at the
                best price it will ever be.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="lp-label-sm" style={{ color: BRONZE }}>Join the founding waitlist</p>
              <p className="text-sm" style={{ color: "#B5AC9E" }}>Be first to know when doors open — and lock in founding pricing.</p>
              <input required placeholder="First name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="fenyx-name"
                className="w-full px-4 py-3 text-sm outline-none"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
              <input required type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="fenyx-email"
                className="w-full px-4 py-3 text-sm outline-none"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
              <input placeholder="Instagram handle (optional)" value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                data-testid="fenyx-instagram"
                className="w-full px-4 py-3 text-sm outline-none"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(168,152,128,0.3)", color: "#F5F1EA" }} />
              <button disabled={busy} data-testid="fenyx-submit"
                className="w-full py-3.5 lp-label-sm transition-opacity hover:opacity-85 disabled:opacity-50"
                style={{ background: BRONZE, color: "#141311" }}>
                {busy ? "Joining…" : "Join the FENYX Waitlist"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
